import React, {useEffect, useState} from "react";
import axios from "axios";
import {AlertTriangle, Bell, Info, RotateCcw, Save, ShieldAlert, Smartphone, Mail, MessageCircle} from "lucide-react";
import * as Constants from "../../utils/Constants";
import * as UrlConstants from "../../utils/UrlConstants";
import {getAuthToken, getClientIdOfUser, showToast} from "../../utils/utility";
import Button from "../../components/ui/Button";
import TextField from "../../components/ui/TextField";
import Card from "../../components/ui/Card";
import Spinner from "../../components/ui/Spinner";
import {cn} from "../../utils/cn";

const CHANNELS = ["WHATSAPP", "EMAIL", "PUSH"];
const CHANNEL_META = {
  WHATSAPP: {label: "WhatsApp", icon: MessageCircle, color: "text-success-600"},
  EMAIL:    {label: "Email",    icon: Mail,           color: "text-info-600"},
  PUSH:     {label: "Push",     icon: Smartphone,     color: "text-brand-600"},
};

const parseChannels = (csv) =>
  csv ? csv.split(",").map((s) => s.trim()).filter(Boolean) : [];

const formatChannels = (arr) => arr.join(",");

const toHHmm = (val, fallback = "08:00") => {
  if (typeof val === "string" && val.length >= 5) return val.substring(0, 5);
  if (val && typeof val === "object") {
    const h = val.hour ?? (Array.isArray(val) ? val[0] : null);
    const m = val.minute ?? (Array.isArray(val) ? val[1] : null);
    if (h != null && m != null) {
      return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    }
  }
  return fallback;
};

const DEFAULTS = {
  whatsappEnabled: false,
  emailEnabled: false,
  pushEnabled: false,
  criticalChannels: [],
  warningChannels: [],
  infoChannels: [],
  dailySummaryTime: "08:00",
  dailySummaryTimezone: "UTC",
  batteryCriticalThresholdPct: 15,
  batteryLowThresholdPct: 30,
  digestIntervalMinutes: 5,
  digestSendTime: "08:00",
};

/**
 * Notification Preferences — redesigned in the design-system idiom.
 * Business logic preserved 1:1: same GET/PUT endpoints, same body
 * shape, same validation, same toast behaviour. Only chrome changes.
 */
const NotificationPreferences = () => {
  const [form, setForm] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const clientId = getClientIdOfUser();
    const url = `${Constants.BASE_URL}${Constants.API_V1}/${clientId}/${UrlConstants.NOTIFICATION_PREFERENCES}`;
    axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
    axios.get(url)
      .then((res) => {
        const p = res.data?.responseData || {};
        setForm({
          whatsappEnabled:             !!p.whatsappEnabled,
          emailEnabled:                !!p.emailEnabled,
          pushEnabled:                 !!p.pushEnabled,
          criticalChannels:            parseChannels(p.criticalChannels),
          warningChannels:             parseChannels(p.warningChannels),
          infoChannels:                parseChannels(p.infoChannels),
          dailySummaryTime:            toHHmm(p.dailySummaryTime, "08:00"),
          dailySummaryTimezone:        p.dailySummaryTimezone || "UTC",
          batteryCriticalThresholdPct: p.batteryCriticalThresholdPct || 15,
          batteryLowThresholdPct:      p.batteryLowThresholdPct      || 30,
          digestIntervalMinutes:       p.digestIntervalMinutes       || 5,
          digestSendTime:              toHHmm(p.digestSendTime, "08:00"),
        });
      })
      .catch((err) => {
        console.error("Notification preferences load failed:", err?.response?.status, err?.response?.data || err?.message);
        showToast(err?.response?.data?.responseMessage || err?.message || "Failed to load notification preferences", {type: "error"});
      })
      .finally(() => setLoading(false));
  }, []);

  const toggleBoolean = (field) =>
    setForm((prev) => ({...prev, [field]: !prev[field]}));

  const toggleChannel = (field, channel) => {
    setForm((prev) => {
      const list = prev[field];
      return {
        ...prev,
        [field]: list.includes(channel) ? list.filter((c) => c !== channel) : [...list, channel],
      };
    });
  };

  const handleChange = (e) => {
    const {name, value} = e.target;
    setForm((prev) => ({...prev, [name]: value}));
  };

  const handleNumberChange = (e) => {
    const {name, value} = e.target;
    setForm((prev) => ({...prev, [name]: parseInt(value, 10) || 0}));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (form.batteryCriticalThresholdPct >= form.batteryLowThresholdPct) {
      showToast("Battery critical threshold must be lower than low threshold", {type: "error"});
      return;
    }
    setSaving(true);
    const clientId = getClientIdOfUser();
    const body = {
      whatsappEnabled:             form.whatsappEnabled,
      emailEnabled:                form.emailEnabled,
      pushEnabled:                 form.pushEnabled,
      criticalChannels:            formatChannels(form.criticalChannels),
      warningChannels:             formatChannels(form.warningChannels),
      infoChannels:                formatChannels(form.infoChannels),
      dailySummaryTime:            form.dailySummaryTime + ":00",
      dailySummaryTimezone:        form.dailySummaryTimezone,
      batteryCriticalThresholdPct: form.batteryCriticalThresholdPct,
      batteryLowThresholdPct:      form.batteryLowThresholdPct,
      digestIntervalMinutes:       form.digestIntervalMinutes,
      digestSendTime:              form.digestSendTime + ":00",
    };
    const url = `${Constants.BASE_URL}${Constants.API_V1}/${clientId}/${UrlConstants.NOTIFICATION_PREFERENCES}`;
    axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
    axios.put(url, {requestBody: body})
      .then(() => showToast("Preferences saved", {type: "success"}))
      .catch((err) => {
        console.error("Notification preferences save failed:", err?.response?.status, err?.response?.data || err?.message);
        showToast(err?.response?.data?.responseMessage || err?.message || "Failed to save preferences", {type: "error"});
      })
      .finally(() => setSaving(false));
  };

  if (loading) {
    return (
      <div className="py-16 flex items-center justify-center gap-3 text-ink-500">
        <Spinner size="md" /><span className="text-sm">Loading preferences…</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-[1100px] mx-auto space-y-6 pb-8">
      {/* ── Global channels ─────────────────────────────────────── */}
      <Card>
        <div className="p-6 border-b border-ink-100">
          <h2 className="text-base font-semibold text-ink-900 flex items-center gap-2">
            <Bell className="h-4 w-4 text-brand-700" />
            Enabled channels
          </h2>
          <p className="text-sm text-ink-500 mt-1">
            Turn each delivery channel on or off globally. Disabled channels won't deliver any alerts.
          </p>
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            {field: "whatsappEnabled", channel: "WHATSAPP"},
            {field: "emailEnabled",    channel: "EMAIL"},
            {field: "pushEnabled",     channel: "PUSH"},
          ].map(({field, channel}) => (
            <ChannelToggle
              key={field}
              checked={form[field]}
              onChange={() => toggleBoolean(field)}
              channel={channel}
            />
          ))}
        </div>
        <p className="px-6 pb-6 -mt-2 text-xs text-ink-500">
          <span className="font-medium text-ink-700">Push</span> sends GPS tracker alerts (engine on/off
          with trip summary, overspeed, power cut, tamper) to the Yekkada Android app of your admins,
          managers and each vehicle&apos;s assigned users. With Push on, overspeed alerts go to the app
          only — no WhatsApp. Each person tunes their own alerts in the app (Profile → Vehicle alerts).
        </p>
      </Card>

      {/* ── Severity ────────────────────────────────────────────── */}
      <Card>
        <div className="p-6 border-b border-ink-100">
          <h2 className="text-base font-semibold text-ink-900">Channels by severity</h2>
          <p className="text-sm text-ink-500 mt-1">
            Alerts are delivered only for GPS locks assigned to an{" "}
            <span className="font-medium text-ink-700">Active Fleet Trip</span>. Critical alerts fire
            immediately; warnings are batched by the digest interval.
          </p>
        </div>
        <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <SeverityGroup
            tone="danger"
            icon={ShieldAlert}
            title="Critical"
            subtitle="Lock · Unlock · Tamper"
            selected={form.criticalChannels}
            onToggle={(ch) => toggleChannel("criticalChannels", ch)}
          />
          <SeverityGroup
            tone="warning"
            icon={AlertTriangle}
            title="Warning"
            subtitle="Low battery · GPS · Geofence"
            selected={form.warningChannels}
            onToggle={(ch) => toggleChannel("warningChannels", ch)}
          />
          <SeverityGroup
            tone="info"
            icon={Info}
            title="Info"
            subtitle="Device online / offline"
            selected={form.infoChannels}
            onToggle={(ch) => toggleChannel("infoChannels", ch)}
          />
        </div>
      </Card>

      {/* ── Thresholds & Daily Summary ──────────────────────────── */}
      <Card>
        <div className="p-6 border-b border-ink-100">
          <h2 className="text-base font-semibold text-ink-900">Thresholds & daily summary</h2>
          <p className="text-sm text-ink-500 mt-1">
            Tune what counts as low battery and how often digests are sent.
          </p>
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <TextField
            label="Battery critical (%)"
            type="number"
            name="batteryCriticalThresholdPct"
            value={form.batteryCriticalThresholdPct}
            onChange={handleNumberChange}
            min={1}
            max={99}
            helper="Below this percent, fire a critical alert."
          />
          <TextField
            label="Battery low (%)"
            type="number"
            name="batteryLowThresholdPct"
            value={form.batteryLowThresholdPct}
            onChange={handleNumberChange}
            min={1}
            max={99}
            helper="Below this, fire a warning."
          />
          <TextField
            label="Daily digest time"
            type="time"
            name="digestSendTime"
            value={form.digestSendTime}
            onChange={handleChange}
            helper="Warning alerts (low battery, GPS lost, offline) are emailed once daily at this time; the WhatsApp daily digest uses it too. Your local time."
          />
          <TextField
            label="Daily summary time"
            type="time"
            name="dailySummaryTime"
            value={form.dailySummaryTime}
            onChange={handleChange}
            helper="When to send the daily roll-up."
          />
          <TextField
            label="Timezone"
            type="text"
            name="dailySummaryTimezone"
            value={form.dailySummaryTimezone}
            onChange={handleChange}
            placeholder="e.g. Asia/Kolkata"
            helper="IANA timezone (e.g. UTC, Asia/Kolkata)."
          />
        </div>
      </Card>

      {/* ── Actions ─────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-end gap-3 pt-2">
        <Button
          type="button"
          variant="secondary"
          icon={RotateCcw}
          disabled={saving}
          onClick={() => setForm(DEFAULTS)}
        >
          Reset to defaults
        </Button>
        <Button
          type="submit"
          variant="primary"
          icon={Save}
          loading={saving}
        >
          Save preferences
        </Button>
      </div>
    </form>
  );
};

/* ─── Internal: large channel toggle card (global enable) ─── */
function ChannelToggle({checked, onChange, channel}) {
  const meta = CHANNEL_META[channel];
  const Icon = meta.icon;
  return (
    <label
      className={cn(
        "flex items-center gap-3 p-4 rounded-lg border cursor-pointer select-none",
        "transition-colors duration-fast",
        checked
          ? "border-brand-500 bg-brand-50/60 ring ring-offset-0"
          : "border-ink-200 bg-white hover:bg-ink-50"
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="h-4 w-4 rounded border-ink-300 cursor-pointer shrink-0"
      />
      <Icon className={cn("h-4 w-4 shrink-0", meta.color)} aria-hidden="true" />
      <span className="text-sm font-medium text-ink-900 truncate">{meta.label}</span>
    </label>
  );
}

/* ─── Internal: per-severity channel picker ─── */
function SeverityGroup({tone = "info", icon: Icon, title, subtitle, selected, onToggle}) {
  const toneClasses = {
    danger:  "bg-danger-50 border-danger-100 text-danger-700",
    warning: "bg-warning-50 border-warning-100 text-warning-700",
    info:    "bg-info-50 border-info-100 text-info-700",
  };
  return (
    <div className="rounded-xl border border-ink-200 bg-white overflow-hidden">
      <div className={cn("flex items-center gap-3 px-4 py-3 border-b", toneClasses[tone])}>
        <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
        <div className="min-w-0">
          <p className="text-sm font-semibold leading-tight truncate">{title}</p>
          <p className="text-xs opacity-80 leading-tight mt-0.5 truncate">{subtitle}</p>
        </div>
      </div>
      <div className="px-4 py-3 flex flex-col gap-2">
        {CHANNELS.map((ch) => {
          const meta = CHANNEL_META[ch];
          const ChIcon = meta.icon;
          const checked = selected.includes(ch);
          return (
            <label
              key={ch}
              className={cn(
                "flex items-center gap-2.5 px-2.5 py-2 rounded-md cursor-pointer select-none",
                "text-sm font-medium transition-colors duration-fast",
                checked ? "bg-brand-50 text-brand-700" : "text-ink-700 hover:bg-ink-50"
              )}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggle(ch)}
                className="h-4 w-4 rounded border-ink-300 cursor-pointer shrink-0"
              />
              <ChIcon className={cn("h-3.5 w-3.5 shrink-0", checked ? "text-brand-700" : meta.color)} aria-hidden="true" />
              <span>{meta.label}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

export default NotificationPreferences;
